import { Suspense, lazy, useState, useEffect } from "react";
import CookieConsent from "react-cookie-consent";
import { Toaster } from "react-hot-toast";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// The courses subdomain (courses.aigeniusautomations.com / courses.aig.local)
// renders an entirely separate app shell. Lazy-loaded so the apex bundle
// doesn't ship the courses code and vice-versa.
const CoursesApp = lazy(() => import("@/courses/CoursesApp"));

function isCoursesHost(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname.startsWith("courses.");
}

import { RequireAdmin } from "@/components/auth/RequireAdmin";
import { UserProfile } from "@/components/auth/UserProfile";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { HomePage } from "@/components/homepage";
import NavigationNew from "@/components/homepage/NavigationNew";
import Footer from "@/components/layout/Footer";
import Navigation from "@/components/layout/Navigation";
import { supabase } from "@/lib/supabase";

// Layout

// Public pages
import AboutPage from "@/pages/AboutPage";
import AnalyticsDashboard from "@/pages/admin/AnalyticsDashboard";
import PermissionMatrixSettings from "@/pages/admin/PermissionMatrixSettings";
import SubscribersAdminPage from "@/pages/admin/SubscribersAdmin";
import BlueprintAISignup from "@/pages/BlueprintAISignup";
// TODO (v3.2): Re-enable auth pages when login/signup is implemented
// import LoginPage from "@/pages/LoginPage";
// import SignUpPage from "@/pages/SignUpPage";
import CalendarNew from "@/pages/CalendarNew";
import GetEstimatePage from "@/pages/GetEstimatePage";
import SystemSettingsPage from "@/pages/SystemSettingsPage";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import FAQ from "@/pages/FAQ";
import CaseStudiesPage from "@/pages/CaseStudiesPage";
import CaseStudyDetailPage from "@/pages/CaseStudyDetailPage";
import ContactPage from "@/pages/ContactPage";
import CoursesLandingPage from "@/pages/CoursesLandingPage";
import UnsubscribePage from "@/pages/UnsubscribePage";
// Vocab is courses-scoped — the provider only wraps CoursesApp below.
import { VocabHoverCardProvider } from "@/components/vocab/VocabHoverCardProvider";

// Protected pages
import Tools from "@/pages/Tools";
import Settings from "@/pages/Settings";
// TODO (v3.3): Re-enable countdown widgets
// import CountdownWidgetsPage from "@/pages/CountdownWidgetsPage";
import ROICalculator from "@/pages/tools/ROICalculator";
import WidgetPage from "@/pages/WidgetPage";

// Tool pages
import ReadinessAssessment from "@/pages/tools/ReadinessAssessment";
import FeasibilityCalculator from "@/pages/tools/FeasibilityCalculator";

// Admin pages
import { analytics } from "@/utils/analytics";

// Scroll to top on route change
function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);

    // Track page view if consent granted
    if (analytics.hasConsent()) {
      analytics.pageView(location.pathname + location.search);
    }
  }, [location.pathname, location.search]);

  return null;
}

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const isDevMode = import.meta.env.VITE_DEV_MODE === "true";

      if (isDevMode) {
        // In dev mode, check for localStorage token
        const token = localStorage.getItem("authToken");
        setIsAuthenticated(!!token);
      } else {
        // Production: check Supabase session
        const {
          data: { session },
        } = await supabase.auth.getSession();
        setIsAuthenticated(!!session);
      }
    } catch (error) {
      console.error("Auth check error:", error);
      setIsAuthenticated(false);
    }
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // TODO (v3.2): Redirect to /login once auth is implemented
  return isAuthenticated ? <>{children}</> : <Navigate to="/" replace />;
}

// Layout wrapper for pages with nav and footer
function MainLayout({
  children,
  isAuthenticated,
  noTopPadding = false,
  useNewNav = false,
  userName,
  userEmail,
  onLogout,
}: {
  children: React.ReactNode;
  isAuthenticated: boolean;
  noTopPadding?: boolean;
  useNewNav?: boolean;
  userName?: string | null;
  userEmail?: string | null;
  onLogout?: () => void;
}) {
  return (
    <div className="min-h-screen">
      {useNewNav ? (
        <NavigationNew
          isAuthenticated={isAuthenticated}
          userName={userName}
          userEmail={userEmail}
          onLogout={onLogout}
        />
      ) : (
        <Navigation isAuthenticated={isAuthenticated} />
      )}
      <div className={noTopPadding ? "" : "pt-24"}>{children}</div>
      <Footer />
    </div>
  );
}

function ApexApp() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // TODO (v3.1): userName/userEmail are tracked here and threaded to NavigationNew
  // via MainLayout. Re-enable the nav auth UI once Supabase auth is fully wired.
  const [userName, setUserName] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  // Initialize analytics if consent already granted
  useEffect(() => {
    if (analytics.hasConsent()) {
      analytics.initialize();
    }
  }, []);

  useEffect(() => {
    checkAuth();

    // Set up auth state listener
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setIsAuthenticated(!!session);
        if (session) {
          setUserName(session.user.user_metadata?.full_name || null);
          setUserEmail(session.user.email || null);
        } else {
          setUserName(null);
          setUserEmail(null);
        }
      },
    );

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  const checkAuth = async () => {
    try {
      const isDevMode = import.meta.env.VITE_DEV_MODE === "true";

      if (isDevMode) {
        // In dev mode, check for localStorage token
        const token = localStorage.getItem("authToken");
        setIsAuthenticated(!!token);
        if (token) {
          setUserName("Dev User");
          setUserEmail("dev@example.com");
        }
      } else {
        // Production: check Supabase session
        const {
          data: { session },
        } = await supabase.auth.getSession();
        setIsAuthenticated(!!session);
        if (session) {
          setUserName(session.user.user_metadata?.full_name || null);
          setUserEmail(session.user.email || null);
        }
      }
    } catch (error) {
      console.error("Auth check error:", error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  // TODO (v3.2): Re-enable when login is implemented
  // const handleLogin = () => {
  //   setIsAuthenticated(true);
  // };

  const handleLogout = async () => {
    try {
      const isDevMode = import.meta.env.VITE_DEV_MODE === "true";

      if (isDevMode) {
        localStorage.removeItem("authToken");
      } else {
        await supabase.auth.signOut();
      }
      setIsAuthenticated(false);
      setUserName(null);
      setUserEmail(null);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      {/* Cookie Consent Banner */}
      <CookieConsent
        location="bottom"
        buttonText="Accept"
        declineButtonText="Decline"
        enableDeclineButton
        cookieName="analytics_consent"
        style={{
          background: "#1e293b",
          padding: "20px",
          alignItems: "center",
        }}
        buttonStyle={{
          background: "#B87333",
          color: "white",
          fontSize: "14px",
          fontWeight: "600",
          padding: "10px 30px",
          borderRadius: "6px",
          cursor: "pointer",
        }}
        declineButtonStyle={{
          background: "transparent",
          color: "#94a3b8",
          fontSize: "14px",
          border: "1px solid #475569",
          padding: "10px 30px",
          borderRadius: "6px",
          cursor: "pointer",
        }}
        expires={365}
        onAccept={() => {
          analytics.setConsent(true);
          analytics.initialize();
        }}
        onDecline={() => {
          analytics.setConsent(false);
        }}
      >
        <span style={{ fontSize: "14px", color: "#e2e8f0" }}>
          We use cookies to improve your experience.{" "}
          <a
            href="/privacy"
            style={{ color: "#B87333", textDecoration: "underline" }}
          >
            Learn more
          </a>
        </span>
      </CookieConsent>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            style: {
              background: "#4CAF50",
            },
          },
          error: {
            style: {
              background: "#f44336",
            },
          },
        }}
      />

      <ScrollToTop />

      <Routes>
        {/* Public routes with main layout */}
        <Route
          path="/"
          element={
            <MainLayout
              isAuthenticated={isAuthenticated}
              noTopPadding={true}
              useNewNav={true}
              userName={userName}
              userEmail={userEmail}
              onLogout={handleLogout}
            >
              <HomePage />
            </MainLayout>
          }
        />

        <Route
          path="/calendar"
          element={
            <MainLayout
              isAuthenticated={isAuthenticated}
              useNewNav={true}
              userName={userName}
              userEmail={userEmail}
              onLogout={handleLogout}
            >
              <ErrorBoundary>
                <CalendarNew />
              </ErrorBoundary>
            </MainLayout>
          }
        />

        <Route
          path="/about"
          element={
            <MainLayout
              isAuthenticated={isAuthenticated}
              noTopPadding={true}
              useNewNav={true}
              userName={userName}
              userEmail={userEmail}
              onLogout={handleLogout}
            >
              <AboutPage />
            </MainLayout>
          }
        />

        <Route
          path="/contact"
          element={
            <MainLayout
              isAuthenticated={isAuthenticated}
              noTopPadding={true}
              useNewNav={true}
              userName={userName}
              userEmail={userEmail}
              onLogout={handleLogout}
            >
              <ContactPage />
            </MainLayout>
          }
        />

        <Route
          path="/case-studies"
          element={
            <MainLayout
              isAuthenticated={isAuthenticated}
              useNewNav={true}
              userName={userName}
              userEmail={userEmail}
              onLogout={handleLogout}
            >
              <CaseStudiesPage />
            </MainLayout>
          }
        />

        <Route
          path="/case-studies/:slug"
          element={
            <MainLayout
              isAuthenticated={isAuthenticated}
              useNewNav={true}
              userName={userName}
              userEmail={userEmail}
              onLogout={handleLogout}
            >
              <CaseStudyDetailPage />
            </MainLayout>
          }
        />

        {/* Public courses landing page — unauthenticated marketing surface
            with an email signup form. The courses.* subdomain has its own
            app shell (CoursesApp); this lives on the apex domain. */}
        <Route
          path="/courses"
          element={
            <MainLayout
              isAuthenticated={isAuthenticated}
              noTopPadding={true}
              useNewNav={true}
              userName={userName}
              userEmail={userEmail}
              onLogout={handleLogout}
            >
              <CoursesLandingPage />
            </MainLayout>
          }
        />

        {/* One-click email unsubscribe — token-based, no login required.
            Linked from the bottom of every transactional/marketing email. */}
        <Route
          path="/unsubscribe"
          element={
            <MainLayout
              isAuthenticated={isAuthenticated}
              noTopPadding={true}
              useNewNav={true}
              userName={userName}
              userEmail={userEmail}
              onLogout={handleLogout}
            >
              <UnsubscribePage />
            </MainLayout>
          }
        />

        {/* Public widget embed route - no layout */}
        <Route path="/widget/:slug" element={<WidgetPage />} />

        {/* Blueprint AI signup page - no layout (has its own) */}
        <Route path="/blueprint-ai-signup" element={<BlueprintAISignup />} />

        {/* TODO (v3.2): Re-enable auth routes when login/signup is implemented */}
        {/* Redirect /login and /signup to home for now */}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/signup" element={<Navigate to="/" replace />} />

        {/* Protected routes with main layout */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <MainLayout isAuthenticated={isAuthenticated}>
                <div className="container mx-auto px-6 py-8">
                  <UserProfile onLogout={handleLogout} />
                </div>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Tools pages - PUBLIC for lead generation */}
        <Route
          path="/tools"
          element={
            <MainLayout
              isAuthenticated={isAuthenticated}
              useNewNav={true}
              userName={userName}
              userEmail={userEmail}
              onLogout={handleLogout}
            >
              <Tools />
            </MainLayout>
          }
        />

        <Route
          path="/tools/roi-calculator"
          element={
            <MainLayout
              isAuthenticated={isAuthenticated}
              useNewNav={true}
              userName={userName}
              userEmail={userEmail}
              onLogout={handleLogout}
            >
              <ROICalculator />
            </MainLayout>
          }
        />

        <Route
          path="/tools/readiness-assessment"
          element={
            <MainLayout
              isAuthenticated={isAuthenticated}
              useNewNav={true}
              userName={userName}
              userEmail={userEmail}
              onLogout={handleLogout}
            >
              <ReadinessAssessment />
            </MainLayout>
          }
        />

        <Route
          path="/tools/feasibility-calculator"
          element={
            <MainLayout
              isAuthenticated={isAuthenticated}
              useNewNav={true}
              userName={userName}
              userEmail={userEmail}
              onLogout={handleLogout}
            >
              <FeasibilityCalculator />
            </MainLayout>
          }
        />

        {/* TODO (v3.3): Re-enable countdown widgets */}
        <Route
          path="/countdown-widgets"
          element={<Navigate to="/" replace />}
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <MainLayout isAuthenticated={isAuthenticated}>
                <Settings />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Pricing route (scrolls to pricing section on homepage) */}
        <Route
          path="/pricing"
          element={
            <MainLayout isAuthenticated={isAuthenticated}>
              <HomePage />
            </MainLayout>
          }
        />

        {/* Public rough-estimate questionnaire (no auth required) */}
        <Route path="/get-estimate" element={<GetEstimatePage />} />

        {/* Legal & Info pages */}
        <Route
          path="/terms"
          element={
            <MainLayout isAuthenticated={isAuthenticated}>
              <TermsOfService />
            </MainLayout>
          }
        />

        <Route
          path="/privacy"
          element={
            <MainLayout isAuthenticated={isAuthenticated}>
              <PrivacyPolicy />
            </MainLayout>
          }
        />

        <Route
          path="/faq"
          element={
            <MainLayout
              isAuthenticated={isAuthenticated}
              useNewNav={true}
              userName={userName}
              userEmail={userEmail}
              onLogout={handleLogout}
            >
              <FAQ />
            </MainLayout>
          }
        />

        {/* Admin Routes - Protected */}
        <Route
          path="/admin"
          element={<Navigate to="/admin/analytics" replace />}
        />

        <Route
          path="/admin/analytics"
          element={
            <RequireAdmin>
              <AnalyticsDashboard />
            </RequireAdmin>
          }
        />

        <Route
          path="/admin/subscribers"
          element={
            <RequireAdmin>
              <SubscribersAdminPage />
            </RequireAdmin>
          }
        />

        <Route
          path="/admin/permissions"
          element={
            <RequireAdmin>
              <PermissionMatrixSettings />
            </RequireAdmin>
          }
        />

        {/* System Settings (Dev Only) */}
        <Route path="/system-settings" element={<SystemSettingsPage />} />

        {/* TODO (v3.2): Re-enable forgot-password when auth is implemented */}
        <Route path="/forgot-password" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

// Hostname-aware shell switch: render the courses app on courses.* and the
// marketing/app shell on the apex. Single bundle, code-split via React.lazy.
function App() {
  if (isCoursesHost()) {
    return (
      <ErrorBoundary>
        <VocabHoverCardProvider>
          <Suspense
            fallback={
              <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B87333]" />
              </div>
            }
          >
            <CoursesApp />
          </Suspense>
        </VocabHoverCardProvider>
      </ErrorBoundary>
    );
  }
  return <ApexApp />;
}

export default App;
